import React, { useState } from 'react';
import { Card, Button, Modal, Select, message, Tag, Collapse } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';
import { apiErrorMessage } from '../../utils/apiError';

const { Option } = Select;
const { Panel } = Collapse;

const StudentCertificatesTab = ({ studentId, certificates, onUpdate, readOnly = false }) => {
  const { user } = useAuthStore();
  const isDeveloperAdmin = ['developer_admin', 'super_admin', 'branch_manager'].includes(user?.role);

  const [modalOpen, setModalOpen] = useState(false);
  const [eligibleCourses, setEligibleCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [regenerating, setRegenerating] = useState({});

  const handleOpen = async () => {
    setLoading(true);
    setSelectedCourseId(null);
    try {
      const res = await api.get(`/students/${studentId}/certificates/eligible-courses`);
      setEligibleCourses(res.data);
      setModalOpen(true);
    } catch {
      message.error('Uygun programlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedCourseId) {
      message.warning('Lütfen bir program seçiniz.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/students/${studentId}/certificates`, { course_id: selectedCourseId });
      message.success('Sertifika oluşturuldu. PDF arka planda hazırlanıyor.');
      setModalOpen(false);
      onUpdate();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Sertifika oluşturulamadı.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerate = async (cert) => {
    setRegenerating(prev => ({ ...prev, [cert.id]: true }));
    try {
      const res = await api.post(`/certificates/${cert.id}/regenerate`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cert.certificate_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('PDF yeniden oluşturuldu ve indiriliyor.');
      onUpdate();
    } catch (error) {
      message.error(apiErrorMessage(error, 'PDF oluşturulamadı.'));
    } finally {
      setRegenerating(prev => ({ ...prev, [cert.id]: false }));
    }
  };

  // Dönemlere göre grupla
  const termGroups = {};
  certificates.forEach((cert) => {
    const key = cert.term_id ?? 0;
    const name = cert.term_name ?? 'Dönem Belirtilmemiş';
    if (!termGroups[key]) termGroups[key] = { name, certs: [] };
    termGroups[key].certs.push(cert);
  });
  const groupKeys = Object.keys(termGroups);

  const renderCert = (cert) => (
    <div key={cert.id} className="flex justify-between items-center p-3 border rounded mb-2">
      <div>
        <strong>{cert.course?.name}</strong>
        <span className="ml-2 text-gray-400 text-sm">{cert.certificate_number}</span>
        <span className="ml-2 text-gray-400 text-sm">{dayjs(cert.issued_at).format('DD.MM.YYYY')}</span>
      </div>
      <div className="flex items-center gap-2">
        {!cert.pdf_path && <Tag color="orange">PDF hazırlanıyor</Tag>}
        {cert.pdf_path && (
          <Button
            size="small"
            onClick={async () => {
              const res = await api.get(`/certificates/${cert.id}/download`, { responseType: 'blob' });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${cert.certificate_number}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            İndir
          </Button>
        )}
        {isDeveloperAdmin && (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={regenerating[cert.id]}
            onClick={() => handleRegenerate(cert)}
          >
            Tekrar Oluştur
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Card
        extra={!readOnly && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            loading={loading}
            onClick={handleOpen}
          >
            Sertifika Ekle
          </Button>
        )}
      >
        {certificates.length === 0 && <p className="text-gray-400">Henüz sertifika yok.</p>}

        {groupKeys.length > 0 && (
          <Collapse defaultActiveKey={groupKeys}>
            {groupKeys.map((key) => (
              <Panel
                key={key}
                header={
                  <span className="font-semibold">
                    {termGroups[key].name}
                    <span className="ml-2 text-gray-400 font-normal text-sm">
                      ({termGroups[key].certs.length} sertifika)
                    </span>
                  </span>
                }
              >
                {termGroups[key].certs.map(renderCert)}
              </Panel>
            ))}
          </Collapse>
        )}
      </Card>

      <Modal
        title="Sertifika Ekle"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleCreate}
        okText="Oluştur"
        cancelText="İptal"
        confirmLoading={submitting}
      >
        {eligibleCourses.length === 0 ? (
          <p className="text-gray-400">
            Sertifika alınabilecek tamamlanmış program bulunmuyor.
          </p>
        ) : (
          <>
            <p className="mb-3 text-gray-500">Tüm projeleri tamamlanan programlar listeleniyor:</p>
            <Select
              placeholder="Program seçiniz"
              style={{ width: '100%' }}
              value={selectedCourseId}
              onChange={setSelectedCourseId}
            >
              {eligibleCourses.map((c) => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
          </>
        )}
      </Modal>
    </>
  );
};

export default StudentCertificatesTab;
